import type { SweetAlertOptions } from 'sweetalert2';

// sweetalert2を動的インポート（重いライブラリのため）
let sweetalertModule: typeof import('sweetalert2') | null = null;
let baseInstance: ReturnType<typeof import('sweetalert2').default.mixin> | null = null;
let toastInstance: ReturnType<typeof import('sweetalert2').default.mixin> | null = null;

const swalCustomClass = {
  popup: 'custom-swal-popup',
  actions: 'custom-swal-actions',
  confirmButton: 'custom-swal-confirm-button',
  cancelButton: 'custom-swal-cancel-button',
};

// sweetalert2を遅延読み込み
const loadSweetAlert = async () => {
  if (!sweetalertModule) {
    sweetalertModule = await import('sweetalert2');
    const sweetalert = sweetalertModule.default;

    // ベースとなるスタイルを調整
    baseInstance = sweetalert.mixin({
      confirmButtonColor: 'var(--color-primary)',
      cancelButtonColor: '#999999',
      denyButtonColor: '#cc3333',
      confirmButtonText: 'OK',
      cancelButtonText: 'キャンセル',
      customClass: swalCustomClass,
      didOpen: () => {
        // data-testidを追加
        const confirmButton = document.querySelector('.custom-swal-confirm-button');
        const cancelButton = document.querySelector('.custom-swal-cancel-button');
        if (confirmButton) {
          confirmButton.setAttribute('data-testid', 'swal-confirm-button');
        }
        if (cancelButton) {
          cancelButton.setAttribute('data-testid', 'swal-cancel-button');
        }
      },
    });

    //トースト
    toastInstance = baseInstance.mixin({
      toast: true,
      position: 'top',
      padding: '0.5rem 1rem',
      showConfirmButton: false,
      timer: 3000,
    });
  }
  return {
    sweetalert: sweetalertModule!.default,
    base: baseInstance!,
    Toast: toastInstance!,
  };
};

/**
 * sweetalert2 のチャンクを先読みする。
 *
 * 動的 import のため、そのページで最初の1回だけチャンク取得ぶん待たされる。送信ボタンの直後に
 * 検証エラーを出す画面では、この待ちがボタンの無反応に見えて「押せていない」と受け取られる。
 * 表示する可能性が高くなった時点（モーダルを開いた時など）で呼んでおくと、出す時には解決済みになる。
 */
const preload = (): void => {
  // 失敗しても実際に表示する時に読み直されるため、ここでは何もしない
  loadSweetAlert().catch(() => undefined);
};

const fire = async (options: SweetAlertOptions) => {
  const { sweetalert } = await loadSweetAlert();
  return sweetalert.fire(options);
};

/**
 * 確認
 * @returns
 */
const confirm = async (options: {
  title?: string;
  text?: string;
  confirmButtonText?: string;
  /** 実行しない側のボタン文言。操作を止める案内で「キャンセル」が不自然な場合に「閉じる」等を渡す */
  cancelButtonText?: string;
  /**
   * 取り消せない操作の確認に使う。実行ボタンをグレー、止める側のボタンを赤にして誤タップを防ぎ、
   * キー操作の初期フォーカスも止める側に置く。
   */
  isConfirmDestructive?: boolean;
}) => {
  const { base } = await loadSweetAlert();
  return base.fire({
    title: options?.title || '確認',
    html: options?.text || '操作を続行します。よろしいですか？',
    confirmButtonText: options?.confirmButtonText || 'OK',
    cancelButtonText: options?.cancelButtonText || 'キャンセル',
    showCancelButton: true,
    ...(options.isConfirmDestructive && {
      focusCancel: true,
      customClass: {
        ...swalCustomClass,
        confirmButton: `${swalCustomClass.confirmButton} custom-swal-button-muted`,
        cancelButton: `${swalCustomClass.cancelButton} custom-swal-button-accent`,
      },
    }),
  });
};

/**
 * 閉じた後に呼び出し側でフォーカスを移す場合に渡す。
 *
 * sweetalert2 は既定（returnFocus: true）で、閉じたあと setTimeout 経由で
 * 「開く前にフォーカスされていた要素」へ focus し、開いた時点のスクロール位置へ戻す。
 * この後片付けは fire() の Promise が解決した後に走るため、解決直後にこちらでスクロールしても
 * 上書きされてしまう（送信ボタンの位置＝画面下部へ戻される）。
 * 自分でフォーカス先を決める場合は復帰そのものが不要なので、opt-in で無効化する。
 */
type SwalFocusOptions = {
  /** 閉じた後に元の要素へフォーカスを戻さない。呼び出し側でフォーカスを移すときに指定する */
  skipReturnFocus?: boolean;
};

/**
 * アラート
 * @returns
 */
const alert = async (options: ({ text: string } | { html: string }) & SwalFocusOptions) => {
  const { base } = await loadSweetAlert();
  return base.fire({
    title: ' ',
    confirmButtonText: '閉じる',
    text: 'text' in options ? options.text : undefined,
    html: 'html' in options ? options.html : undefined,
    ...(options.skipReturnFocus && { returnFocus: false }),
  });
};

/**
 * 正常完了トースト
 * @returns
 */
const toastSuccess = async ({ title = '登録に成功しました' } = {}) => {
  const { Toast } = await loadSweetAlert();
  return Toast.fire({
    icon: 'success',
    title: title,
  });
};

/**
 * 入力エラー
 * @returns
 */
const invalid = async () => {
  const { base } = await loadSweetAlert();
  return base.fire({
    title: ' ',
    confirmButtonText: 'OK',
    text: '入力内容を確認してください。',
  });
};

/**
 * エラーメッセージ
 * @returns
 */
const messages = async (options: { messages: Array<string> }) => {
  const { base } = await loadSweetAlert();
  return base.fire({
    title: ' ',
    confirmButtonText: 'OK',
    html: options.messages.join('<br>'),
  });
};

export const swal = {
  preload,
  fire,
  confirm,
  alert,
  invalid,
  messages,
  toastSuccess,
};
